import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { VehiclePipelineStages, type PipelineStage } from '@/components/vehicles/VehiclePipeline';
import { cn } from '@/lib/utils';
import {
  Phone,
  MapPin,
  Calendar,
  MessageSquarePlus,
  CheckCircle2,
  Loader2,
  X,
  StickyNote,
  User,
  Mail,
  Briefcase,
  GraduationCap,
  Home,
  Car,
  Banknote,
  Clock,
  FileText,
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { toast } from 'sonner';
import {
  listDriverApplications,
  getDriverApplicationCounts,
  markDriverApplicationContacted,
  undoDriverApplicationContacted,
  addDriverApplicationNote,
  type DriverApplication,
  type DriverApplicationGroup,
  type DriverApplicationCounts,
} from '@/integrations/supabase/website';

// ─── Stage config ─────────────────────────────────────────────────────────────

type AppStage = 'new' | 'noted' | 'contacted';

const STAGE_CONFIG: { key: AppStage; label: string; color: string }[] = [
  { key: 'new',       label: 'New',       color: 'blue' },
  { key: 'noted',     label: 'Noted',     color: 'amber' },
  { key: 'contacted', label: 'Contacted', color: 'green' },
];

const STAGE_BADGE_STYLES: Record<AppStage, string> = {
  new:       'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
  noted:     'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
  contacted: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400',
};

const STAGE_LABELS: Record<AppStage, string> = {
  new: 'New', noted: 'Noted', contacted: 'Contacted',
};

// Region-based group appended after the status groups. Apps whose region is not
// "Greater Accra" move out of new/noted/contacted into this bucket (partition).
const OTHER_REGIONS_KEY = 'other_regions' as const;
const OTHER_REGIONS_LABEL = 'Other Regions';

// Must match DRIVER_PAGE_SIZE in the website-proxy edge function — pagination is
// served by the backend, this only drives the page-count math on the client.
const ITEMS_PER_PAGE = 20;

const EMPTY_COUNTS: DriverApplicationCounts = {
  all: 0, new: 0, noted: 0, contacted: 0, other_regions: 0,
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const style = STAGE_BADGE_STYLES[status as AppStage];
  const label = STAGE_LABELS[status as AppStage];
  if (style && label) return <Badge variant="outline" className={style}>{label}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

// ─── Detail Field ─────────────────────────────────────────────────────────────

function DetailField({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-sm mt-0.5">{value}</p>
      </div>
    </div>
  );
}

// ─── Detail Sheet ─────────────────────────────────────────────────────────────

function ApplicationDetailSheet({
  application,
  open,
  onOpenChange,
  onApplicationChange,
}: {
  application: DriverApplication | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplicationChange?: (app: DriverApplication) => void;
}) {
  const queryClient = useQueryClient();
  const [noteText, setNoteText] = useState(application?.notes || '');

  // After a mutation the row may move between groups/pages, so refresh both the
  // paged list and the badge counts, and update the open sheet from the returned
  // row so its status/footer stay accurate without relying on the current page.
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['driver-applications'] });
    queryClient.invalidateQueries({ queryKey: ['driver-application-counts'] });
  }, [queryClient]);

  // Sync noteText when application changes (e.g. after refetch)
  useEffect(() => {
    setNoteText(application?.notes || '');
  }, [application?.id, application?.notes]);

  const contactMutation = useMutation({
    mutationFn: async () => {
      const result = await markDriverApplicationContacted(application!.id);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (res) => {
      toast.success('Marked as contacted');
      if (res?.data) onApplicationChange?.(res.data);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const undoContactMutation = useMutation({
    mutationFn: async () => {
      const result = await undoDriverApplicationContacted(application!.id);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (res) => {
      toast.success('Undo successful');
      if (res?.data) onApplicationChange?.(res.data);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const noteMutation = useMutation({
    mutationFn: async () => {
      const result = await addDriverApplicationNote(application!.id, noteText);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (res) => {
      toast.success('Note saved');
      if (res?.data) onApplicationChange?.(res.data);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!application) return null;

  const a = application;
  const fullName = `${a.first_name} ${a.last_name}`.trim();
  const hasExistingNote = !!a.notes;
  const noteChanged = noteText.trim() !== (a.notes || '');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-3xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b bg-muted/30 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="relative z-10 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight">{fullName}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={a.status} />
                  <span className="text-xs text-muted-foreground">
                    Applied {format(new Date(a.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Contact Information</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Phone} label="Phone" value={a.phone} />
                <DetailField icon={Mail} label="Email" value={a.email} />
                <DetailField icon={Home} label="Address" value={a.address} />
                <DetailField icon={MapPin} label="Region" value={a.region} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Personal Details</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                {/* `date_of_birth` and `start_date` are date-only calendar strings, not instants: the
                    applicant picks them in an `<input type="date">` and fleet-website stores them
                    verbatim behind a `/^\d{4}-\d{2}-\d{2}$/` guard (src/lib/validations/driver.ts).
                    `new Date('1994-03-04')` is UTC midnight, so a negative-offset browser printed the
                    day before — a wrong DOB on a hire-purchase party. */}
                <DetailField icon={Calendar} label="Date of Birth" value={a.date_of_birth ? format(parse(a.date_of_birth.substring(0, 10), 'yyyy-MM-dd', new Date()), 'MMM d, yyyy') : null} />
                <DetailField icon={GraduationCap} label="Education" value={a.education_level} />
                <DetailField icon={Home} label="Residence Duration" value={a.residence_duration} />
                <DetailField icon={Home} label="Ownership Status" value={a.ownership_status} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Employment</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Briefcase} label="Employment Status" value={a.employment_status} />
                <DetailField icon={Briefcase} label="Employer" value={a.employer_name} />
                <DetailField icon={Clock} label="Years with Employer" value={a.years_with_employer} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Driving Information</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={FileText} label="License Number" value={a.license_number} />
                <DetailField icon={Car} label="License Class" value={a.license_class} />
                <DetailField icon={Clock} label="Years Experience" value={a.years_experience} />
                <DetailField icon={Car} label="Ride-Hailing Experience" value={a.ride_hailing_experience} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Financial</h3>
              <div className="rounded-lg border bg-muted/20 p-4 space-y-1">
                <DetailField icon={Banknote} label="Deposit Amount" value={a.deposit_amount} />
                <DetailField icon={Calendar} label="Preferred Start Date" value={a.start_date ? format(parse(a.start_date.substring(0, 10), 'yyyy-MM-dd', new Date()), 'MMM d, yyyy') : null} />
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Admin Note</h3>
              <div className="space-y-2">
                <Textarea
                  placeholder="Add a note about this applicant..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => noteMutation.mutate()}
                  disabled={!noteChanged || noteMutation.isPending}
                >
                  {noteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <StickyNote className="h-4 w-4 mr-1.5" />
                  )}
                  {hasExistingNote ? 'Save Note' : 'Add Note'}
                </Button>
              </div>
            </div>

            {a.contacted_at && (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Contacted on {format(new Date(a.contacted_at), 'MMM d, yyyy \'at\' h:mm a')}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t bg-background/80 backdrop-blur-md flex items-center justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            Close
          </Button>

          {a.status === 'contacted' ? (
            <Button
              variant="outline"
              onClick={() => undoContactMutation.mutate()}
              disabled={undoContactMutation.isPending}
              className="rounded-full"
            >
              {undoContactMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <X className="h-4 w-4 mr-1.5" />
              )}
              Undo Contacted
            </Button>
          ) : (
            <Button
              onClick={() => contactMutation.mutate()}
              disabled={contactMutation.isPending}
              className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
            >
              {contactMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
              )}
              Mark Contacted
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DriverApplications() {
  const [stageFilter, setStageFilter] = useState<DriverApplicationGroup | null>('new');
  const [selectedApp, setSelectedApp] = useState<DriverApplication | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Exact per-group counts for the pipeline badges, fetched independently of the
  // current page so the tallies stay correct under server-side pagination.
  const { data: counts = EMPTY_COUNTS } = useQuery({
    queryKey: ['driver-application-counts'],
    queryFn: async () => {
      const result = await getDriverApplicationCounts();
      if (result.error) throw new Error(result.error);
      return result.data!.counts;
    },
    placeholderData: keepPreviousData,
  });

  // Pipeline stages: the three status groups (Greater Accra), then Other Regions.
  const pipelineStages: PipelineStage[] = [
    ...STAGE_CONFIG.map((s) => ({ ...s, count: counts[s.key] })),
    { key: OTHER_REGIONS_KEY, label: OTHER_REGIONS_LABEL, color: 'slate', count: counts.other_regions },
  ];

  // Rows in the active group drive pagination (All => counts.all).
  const activeGroupCount = stageFilter ? counts[stageFilter] : counts.all;
  const { currentPage, totalPages, totalItems, goToPage, resetPage } = useServerPagination({
    totalCount: activeGroupCount,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  // Current page of the active group — already paginated by the edge function.
  const { data: listResult, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['driver-applications', stageFilter, currentPage],
    queryFn: async () => {
      const result = await listDriverApplications(currentPage, stageFilter ?? undefined);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
    placeholderData: keepPreviousData,
  });

  const applications = listResult?.data ?? [];

  // keepPreviousData keeps the pager/header stable across navigation, but the
  // held rows belong to the PREVIOUS page/group until the new fetch lands.
  // isPlaceholderData is true exactly in that window (and false for a same-page
  // refetch, e.g. after a mutation), so we shimmer the rows instead of showing
  // the old page, then swap in the loaded page.
  const showingPlaceholderRows = isPlaceholderData;

  // Don't strand the user past the last page after a count shrinks (e.g. a
  // mutation moved the final row of a page into another group).
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) goToPage(totalPages);
  }, [totalPages, currentPage, goToPage]);

  const handleRowClick = useCallback((app: DriverApplication) => {
    setSelectedApp(app);
    setSheetOpen(true);
  }, []);

  const handleStageClick = useCallback((key: string | null) => {
    setStageFilter(key as DriverApplicationGroup | null);
    resetPage();
  }, [resetPage]);

  const emptyMessage =
    stageFilter === OTHER_REGIONS_KEY
      ? 'No applications from other regions.'
      : stageFilter
        ? `No ${STAGE_LABELS[stageFilter as AppStage].toLowerCase()} applications.`
        : 'No applications have been submitted yet.';

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2">Driver Applications</h1>
          <p className="text-muted-foreground">Review and manage driver applications from the website.</p>
        </div>
      </div>

      {/* Staged view + Table */}
      <Card className="border-0">
        <CardContent className="space-y-4">
          {/* Pipeline stages */}
          <VehiclePipelineStages
            stages={pipelineStages}
            activeStage={stageFilter}
            onStageClick={handleStageClick}
            groupCount={counts.all}
          />

          {/* Table */}
          {error ? (
            <div className="text-center py-12 text-destructive">
              <p className="text-sm font-medium">Failed to load applications</p>
              <p className="text-xs text-muted-foreground mt-1">{(error as Error).message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <>
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                onPageChange={goToPage}
              />
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead>Name</SortableTableHead>
                      <SortableTableHead>Phone</SortableTableHead>
                      <SortableTableHead>Region</SortableTableHead>
                      <SortableTableHead>License</SortableTableHead>
                      <SortableTableHead>Deposit</SortableTableHead>
                      <SortableTableHead>Status</SortableTableHead>
                      <SortableTableHead>Applied</SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {showingPlaceholderRows ? (
                      [...Array(applications.length || 8)].map((_, i) => (
                        <TableRow key={`skeleton-${i}`}>
                          <TableCell colSpan={7} className="py-3">
                            <Skeleton className="h-10 w-full" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : applications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                          {emptyMessage}
                        </TableCell>
                      </TableRow>
                    ) : (
                      applications.map((app) => (
                        <TableRow
                          key={app.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleRowClick(app)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <span className="text-xs font-medium text-primary">
                                  {app.first_name[0]}{app.last_name[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{app.first_name} {app.last_name}</p>
                                <p className="text-xs text-muted-foreground">{app.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{app.phone}</TableCell>
                          <TableCell className="text-sm">{app.region}</TableCell>
                          <TableCell className="text-sm">{app.license_class}</TableCell>
                          <TableCell className="text-sm">{app.deposit_amount}</TableCell>
                          <TableCell>
                            <StatusBadge status={app.status} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {format(new Date(app.created_at), 'MMM d, yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <ApplicationDetailSheet
        application={selectedApp}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onApplicationChange={setSelectedApp}
      />
    </div>
  );
}
